import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './useToast';
import { useI18n } from '../i18n';
import {
  getTournament, getBouts, getTournamentRegistrations,
  getTournamentReferees, getTournamentManagers, getBrackets,
} from '../api/client';
import api from '../api/client';
import { useAuthStore } from '../store/auth';
import { useSocket, useRooms, bindSocket } from './useSocket';
import type { SocketHandlers } from './useSocket';
import { logBackgroundError } from '../observability';
import { useOnResume } from '../lib/resume';
import { enumerateDays } from '../utils/calendarDate';
import type {
  Tournament, Bout, Registration, TournamentReferee, Bracket,
  JuryAssignment, TournamentManager, Ring, WeighinAthlete,
  SocketRegistrationUpdated, SocketBoutStatusChanged,
  SocketWeighinUpdated, SocketWeighinEnded, SocketWeighinEndCancelled, SocketWeighinEndProposed, WeighinProposal,
  SocketRegistrationsChanged, SocketBoutUpdated, SocketTournamentUpdated, SocketBracketsUpdated, SocketRefereesUpdated,
  WeighinProposalsByDay,
} from '../types';

/**
 * A read of one state under the ticket of its writers: the read takes a
 * number when it starts, and its answer writes only while the number is
 * still the newest (#412, #419, #459, #591). The promise rejects when the
 * read fails: the caller decides what to do with the failure.
 */
function readUnder<T>(ticket: { current: number }, read: () => Promise<{ data: T }>, apply: (data: T) => void): Promise<void> {
  const seq = ++ticket.current;
  return read().then(r => { if (seq === ticket.current) apply(r.data); });
}

/**
 * Core tournament data hook — owns the shared state that all tabs need.
 * Tab-specific UI state (form toggles, modals) stays local in each tab component.
 */
export function useTournamentData(id: string | undefined) {
  const { user } = useAuthStore();
  const socket = useSocket();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [bouts, setBouts] = useState<Bout[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [referees, setReferees] = useState<TournamentReferee[]>([]);
  const [juryAssignments, setJuryAssignments] = useState<JuryAssignment[]>([]);
  const [managers, setManagers] = useState<TournamentManager[]>([]);
  const [brackets, setBrackets] = useState<Bracket[]>([]);
  const [boutSubs, setBoutSubsState] = useState<Set<string>>(new Set());
  // #593 — the bell of the bout list changes the set at once. A load of the
  // set applies the answer of the server, and then the taps that came after
  // the start of the load. Before, one tap dropped the whole answer, and the
  // other follows showed "Notify me" until the next reload (the logic review
  // of PR #598). A tap whose request has no answer yet when a load starts
  // applies again too: the read of the load can come before the write of
  // the tap. Only the newest load applies.
  const subsTaps = useRef<Array<{ seq: number; id: string; on: boolean; settled: boolean }>>([]);
  const subsTapSeq = useRef(0);
  const subsLoadSeq = useRef(0);
  /** Shows a tap at once. Call the function that it returns when the request of the tap answered. */
  const setBoutSub = useCallback((boutId: string, on: boolean) => {
    const tap = { seq: ++subsTapSeq.current, id: boutId, on, settled: false };
    subsTaps.current.push(tap);
    setBoutSubsState(prev => {
      if (prev.has(boutId) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(boutId); else next.delete(boutId);
      return next;
    });
    return () => { tap.settled = true; };
  }, []);
  const [loading, setLoading] = useState(true);
  // #457 — the first load failed. Before, one failed GET of the six left the
  // skeleton on screen for ever, and the page never said why.
  const [loadError, setLoadError] = useState<unknown>(null);
  const { showError, showSuccess, showWarning, showToast } = useToast();
  const [weighins, setWeighins] = useState<WeighinAthlete[]>([]);
  // #556 — "ended" for each day, as the proposals are. One flag for the
  // tournament marked day 2 as ended on screen after the End of day 1.
  const [weighinEndedDays, setWeighinEndedDays] = useState<Record<string, boolean>>({});
  const setWeighinEnded = useCallback((date: string, ended: boolean) => {
    setWeighinEndedDays(prev => (prev[date] === ended ? prev : { ...prev, [date]: ended }));
  }, []);
  // #548 — the open End proposal of each day. The Weigh-in tab loads it with
  // the list of a day (GET /:id/weighins), and the socket events below
  // change it. #548 review — keyed by the day: one slot for the tournament
  // let the proposal of day 1 replace the open proposal of day 2, and the
  // banner of day 2 disappeared. The name stays singular for the page that
  // passes it on: read it as `weighinProposal[day]`.
  const [weighinProposal, setWeighinProposals] = useState<WeighinProposalsByDay>({});
  const setWeighinProposal = useCallback((day: string, proposal: WeighinProposal | null) => {
    setWeighinProposals(prev => (prev[day] === proposal ? prev : { ...prev, [day]: proposal }));
  }, []);

  /** Sequence number of the newest reload (#412). A reload whose requests
   *  left EARLIER must never overwrite state written by a newer one.
   *
   *  Measured 2026-09-22: joining the referee pool set `referees` to the
   *  fresh list, and a `reload()` already in flight then put it back to the
   *  empty one — so the page showed "Join pool" and "No referees in pool
   *  yet" while the row sat in the database. `reload` is a useCallback keyed
   *  on [id, user], and the load effect re-runs it whenever that identity
   *  changes, so an older response landing last is ordinary, not exotic. */
  const reloadSeq = useRef(0);
  /** Sequence number of the newest request that will write `referees`
   *  (#419). Two writers exist: `reload()` and the `referees:updated`
   *  handler below. The guard above orders reloads among themselves only,
   *  so a reload that left BEFORE a join could still land AFTER the
   *  handler's fetch and put the pre-join list back — the #412 symptom, on
   *  the manager's screen. Every writer takes a number at its start and
   *  writes only while it is still the newest. */
  const refereesSeq = useRef(0);
  /** #459 — the same guard for `registrations` and `bouts`: `reload()`, the
   *  targeted refetches below and the in-place socket patches all write them. */
  const registrationsSeq = useRef(0);
  const boutsSeq = useRef(0);
  /** #664 — a live or local write of the bouts or the registrations: newer
   *  than every fetch in flight, as `patchTournament` is for the tournament.
   *  The hook returns no bare setter of these lists (#490 did the same for the
   *  tournament): a fetch that left before the write must not land after it. */
  const patchBouts = useCallback((fn: (prev: Bout[]) => Bout[]) => {
    ++boutsSeq.current;
    setBouts(fn);
  }, []);
  const patchRegistrations = useCallback((fn: (prev: Registration[]) => Registration[]) => {
    ++registrationsSeq.current;
    setRegistrations(fn);
  }, []);
  /** #490 — the same problem for `tournament`, with a merge instead of a
   *  drop. A reload wrote the tournament with no guard against the live
   *  `tournament:updated`, so a reload that left before a lock landed after
   *  it and showed the tournament unlocked again (the full e2e suite caught
   *  it on 2026-09-26). A fetch cannot simply be dropped: it carries fields
   *  that a live payload does not, and before the first load there is
   *  nothing to keep. So every live or local write gets a number and goes in
   *  this log, and a fetch lands UNDER the writes that came after it left. */
  const tournamentSeq = useRef(0);
  const tournamentPatches = useRef<Array<{ seq: number; patch: Partial<Tournament> }>>([]);

  /** A live or local write: newer than every fetch in flight. */
  const patchTournament = useCallback((patch: Partial<Tournament>) => {
    const seq = ++tournamentSeq.current;
    tournamentPatches.current = [...tournamentPatches.current.slice(-49), { seq, patch }];
    setTournament(prev => (prev ? { ...prev, ...patch } : prev));
  }, []);

  /** A fetched tournament. `since` is `tournamentSeq` when the fetch left;
   *  the writes after it go on top, so the older answer cannot undo them. */
  const landTournament = useCallback((fetched: Tournament, since: number) => {
    const newer = tournamentPatches.current.filter(p => p.seq > since);
    setTournament(newer.reduce<Tournament>((acc, p) => ({ ...acc, ...p.patch }), fetched));
  }, []);

  /** The tournament whose data is on screen. A load for it refreshes behind
   *  the page; only a first load shows the skeleton (#457 review). */
  const shownFor = useRef<string | null>(null);
  /** The newest initial load: only it may report an error. */
  const loadSeq = useRef(0);

  const reload = useCallback(async () => {
    if (!id) return;
    const seq = ++reloadSeq.current;
    const rseq = ++refereesSeq.current;
    const gseq = ++registrationsSeq.current;
    const bseq = ++boutsSeq.current;
    const tsince = tournamentSeq.current;
    const [t, b, r, refs, jury, mgrs] = await Promise.all([
      getTournament(id),
      getBouts(id),
      getTournamentRegistrations(id),
      getTournamentReferees(id),
      api.get(`/api/tournaments/${id}/jury`),
      getTournamentManagers(id),
    ]);
    // A newer reload started while these were in flight. Its answer is the
    // current one; dropping this stale set is the whole fix.
    if (seq !== reloadSeq.current) return;
    landTournament(t.data, tsince);
    // The data of this tournament is on screen: any applied reload ends the
    // first load, so a stale first reload cannot clear the skeleton early
    // and flash "Tournament not found".
    shownFor.current = id;
    setLoading(false);
    if (bseq === boutsSeq.current) setBouts(b.data);
    if (gseq === registrationsSeq.current) setRegistrations(r.data);
    // A newer referees fetch (#419) may have landed while this one was in
    // flight; the other five fields have no second writer and always land.
    if (rseq === refereesSeq.current) setReferees(refs.data);
    setJuryAssignments(jury.data);
    setManagers(mgrs.data);
    if (user) {
      const load = ++subsLoadSeq.current;
      const since = subsTapSeq.current;
      const open = new Set(subsTaps.current.filter(tap => !tap.settled).map(tap => tap.seq));
      api.get(`/api/bouts/tournament/${id}/my-subscriptions`)
        .then(r => {
          if (load !== subsLoadSeq.current) return;
          const next = new Set<string>(r.data);
          for (const tap of subsTaps.current) {
            if (tap.seq <= since && !open.has(tap.seq)) continue;
            if (tap.on) next.add(tap.id); else next.delete(tap.id);
          }
          // A later load reads the result of every settled tap from before
          // the start of this load.
          subsTaps.current = subsTaps.current.filter(tap => tap.seq > since || !tap.settled);
          setBoutSubsState(next);
        })
        .catch(logBackgroundError('useTournamentData:bout-subs'));
    }
  }, [id, user, landTournament]);

  /** A fetch of the tournament alone, after a change of the page (a ring). */
  const refreshTournament = useCallback(async () => {
    if (!id) return;
    const since = tournamentSeq.current;
    const fresh = await getTournament(id);
    landTournament(fresh.data, since);
  }, [id, landTournament]);

  // Initial load. Also the Retry of the error state. It runs again when
  // `user` changes (a role change, the getMe of a page load): that run
  // refreshes behind the page, as before #457 — it must not show the
  // skeleton, which closed every tab and modal mid-session.
  const loadInitial = useCallback(() => {
    if (!id) return;
    const seq = ++loadSeq.current;
    if (shownFor.current !== id) setLoading(true);
    setLoadError(null);
    reload().catch(err => {
      logBackgroundError('useTournamentData:load')(err);
      // A newer load owns the flags; data on screen stays on screen.
      if (seq !== loadSeq.current || shownFor.current === id) return;
      setLoadError(err);
      setLoading(false);
    });
  }, [id, reload]);
  useEffect(() => { loadInitial(); }, [loadInitial]);
  // #499 — after a gap in the socket (an iOS app that stopped), the events of
  // the gap are lost. Load again through `loadInitial`: data on screen
  // refreshes behind the page, a first load keeps its skeleton and reports its
  // own error, and an error screen retries.
  // #591 — the brackets read again: the list, and (through the version) each
  // open tree of the Brackets tab. A socket event and a resume call it.
  const bracketsSeq = useRef(0);
  const [bracketsVersion, setBracketsVersion] = useState(0);
  /** #664 — the one read of the brackets list. A failure rejects, so a caller
   *  after a write (`useAfterWrite`) can say that the list did not refresh.
   *  It does not bump the version: it reads the list, not the open trees. */
  const loadBrackets = useCallback(async () => {
    if (!id) return;
    await readUnder(bracketsSeq, () => getBrackets(id), setBrackets);
  }, [id]);
  /** The list, and each open tree through the version. A socket event and a
   *  resume call it; a failure goes to the telemetry. */
  const refreshBrackets = useCallback(() => {
    if (!id) return;
    loadBrackets().catch(logBackgroundError('useTournamentData:brackets-refresh'));
    setBracketsVersion(v => v + 1);
  }, [id, loadBrackets]);
  const onResume = useCallback(() => { loadInitial(); refreshBrackets(); }, [loadInitial, refreshBrackets]);
  useOnResume(onResume, 'tournament');

  // Load brackets. #591 — the first read takes a number of `bracketsSeq` too:
  // a slow first answer must not overwrite a newer refresh, and a change of the
  // tournament in place drops a late answer of the previous one.
  useEffect(() => {
    loadBrackets().catch(logBackgroundError('useTournamentData:brackets'));
  }, [loadBrackets]);

  // #664 (F49) — the room is a key of the id, not of this effect: the effect
  // below runs again when `reload` changes (a role refresh), and the room stays.
  useRooms([id ? `tournament:${id}` : null]);

  // Socket events
  useEffect(() => {
    if (!socket || !id) return;
    // #459 — a bout edit without a status change, and a new or a removed
    // registration: a TARGETED refetch, coalesced. A bulk registration sends
    // one event for each athlete, and reload() is six GETs on every viewer
    // (the referees handler below records what that did to the e2e suite).
    let regTimer: ReturnType<typeof setTimeout> | null = null;
    let boutTimer: ReturnType<typeof setTimeout> | null = null;
    const onRegistrationsChanged = ({ tournamentId }: SocketRegistrationsChanged) => {
      if (tournamentId && tournamentId !== id) return;
      if (regTimer) clearTimeout(regTimer);
      regTimer = setTimeout(() => {
        readUnder(registrationsSeq, () => getTournamentRegistrations(id), setRegistrations)
          .catch(logBackgroundError('useTournamentData:registrations-changed'));
      }, 300);
    };
    const onBoutUpdated = ({ tournamentId }: SocketBoutUpdated) => {
      if (tournamentId && tournamentId !== id) return;
      if (boutTimer) clearTimeout(boutTimer);
      boutTimer = setTimeout(() => {
        readUnder(boutsSeq, () => getBouts(id), setBouts)
          .catch(logBackgroundError('useTournamentData:bout-updated'));
        // A bout edit can put an athlete into a bout or take one out, and a
        // plan reassignment renumbers the bouts. The Boxers tab shows the bout
        // numbers of each athlete from the registrations, so they refresh too:
        // they stayed stale on every open page until a reload (2026-09-25).
        readUnder(registrationsSeq, () => getTournamentRegistrations(id), setRegistrations)
          .catch(logBackgroundError('useTournamentData:bout-updated-registrations'));
      }, 300);
    };
    const onTournamentUpdated = (updated: SocketTournamentUpdated) => {
      // The page can move to another tournament in place (no route key), so
      // a late row of the previous one must not go into this one's log.
      if (updated.id && updated.id !== id) return;
      patchTournament(updated);
    };
    const onWeighinUpdated = ({ registrationId, status, weightKg, date }: SocketWeighinUpdated) => {
      // #549 — the weight too: the Weigh-in tab of another manager shows it.
      // #548 review — only the row of the same day: an athlete with a bout
      // on two days has a row in the list of each day, and a mark of day 1
      // put its status and weight into the row of day 2.
      setWeighins(prev => prev.map(w => w.registration_id === registrationId
        && (!date || !w.weigh_in_date || w.weigh_in_date === date)
        ? { ...w, weigh_in_status: status, ...(weightKg !== undefined ? { weigh_in_weight: weightKg } : {}) }
        : w));
      reload();
    };
    const onWeighinEnded = ({ woCount, date }: SocketWeighinEnded) => {
      if (date) setWeighinEnded(date, true);
      // The End takes the proposal of its day. No day: clear them all.
      if (date) setWeighinProposal(date, null); else setWeighinProposals({});
      // The store, not the hook: the socket handler outlives a language change.
      showSuccess(useI18n.getState().t.tournament.weighinEndedToast.replace('{n}', String(woCount)));
      reload();
    };
    // #548 — a day has its own proposal: an event changes only its day.
    const onWeighinProposed = (data: SocketWeighinEndProposed) => {
      if (data?.date) setWeighinProposal(data.date, data);
    };
    const onWeighinCancelled = ({ date }: SocketWeighinEndCancelled) => {
      if (date) setWeighinProposal(date, null); else setWeighinProposals({});
    };
    // Patch registrations[] in place when another TM toggles is_confirmed /
    // age_group / weight_class. Keeps the Confirmed pill (RegistrationsTab)
    // live without a full reload — parity with how bout:created is handled.
    const onRegistrationUpdated = (data: SocketRegistrationUpdated) => {
      // A fetch in flight predates this change: it must not land after it.
      patchRegistrations(prev => prev.map(r => r.id === data.id
        ? { ...r, is_confirmed: data.is_confirmed, age_group: data.age_group, weight_class: data.weight_class }
        : r
      ));
    };
    // bouts.ts emits bout:status_changed to BOTH the tournament room and the
    // bout room ("so all viewers update live"), but the tournament half had no
    // listener — so a bout set to 'current' by hand, or by Start Day, left
    // this list stale until a manual reload. The auto-advance path hid it,
    // because bout:advanced IS handled above.
    //
    // Patch in place; do NOT call reload(). Start Day emits one event PER
    // first bout (bouts.ts:499), so a reload() here would fire one
    // six-endpoint refetch per ring, on every connected client, at the exact
    // moment the tournament goes live.
    // Refetch the REFEREES only — never `reload()`, which is six endpoints.
    // I wrote reload() here first and the open-ring lifecycle went from 1
    // retry to 17 and 2.3 minutes slower: that spec joins five officials in
    // sequence, so each join fired a six-endpoint refetch on every connected
    // client. `bout:status_changed` carries the same warning a few lines
    // below, written after Start Day did exactly this per ring.
    const onRefereesUpdated = ({ tournamentId }: SocketRefereesUpdated) => {
      if (tournamentId && tournamentId !== id) return;
      if (!id) return;
      readUnder(refereesSeq, () => getTournamentReferees(id), setReferees)
        .catch(logBackgroundError('useTournamentData:referees-updated'));
    };
    const onBoutStatusChanged = ({ boutId, status, tournamentId }: SocketBoutStatusChanged) => {
      if (tournamentId && tournamentId !== id) return;
      if (!status) return;
      patchBouts(prev => prev.map(b => (b.id === boutId ? { ...b, status } : b)));
    };
    // The data handlers, in one call (#664, F83). Some of these events also
    // run the bracket handler below. That handler is a second call, because a
    // map holds one listener for each event.
    const unbindData = bindSocket(socket, {
      'bout:scored': reload,
      'bout:advanced': reload,
      'bout:created': reload,
      'bout:deleted': reload,
      'bout:updated': onBoutUpdated,
      'registrations:changed': onRegistrationsChanged,
      'tournament:updated': onTournamentUpdated,
      'weighin:updated': onWeighinUpdated,
      'weighin:ended': onWeighinEnded,
      'weighin:end_proposed': onWeighinProposed,
      'weighin:end_cancelled': onWeighinCancelled,
      'registration:updated': onRegistrationUpdated,
      'bout:status_changed': onBoutStatusChanged,
      // #415 — the referee pool had no listener at all, so a tournament
      // manager watching the Officials tab never saw officials arrive. The
      // payload carries only the id. The handler and `reload()` share
      // `refereesSeq` (#419), so neither a burst of joins nor a reload already
      // in flight can leave the list on an older answer.
      'referees:updated': onRefereesUpdated,
    });
    // #591 — a bracket write sends `brackets:updated`. A result, a start, a bout
    // change and the walk-overs of an End move a winner or the live mark in a
    // tree. The list and each open tree read again, coalesced, after a random
    // 0.3 to 1.8 s, so that the viewers do not all read at one moment (the
    // reconnect reload of useSocket spreads the same way).
    let bracketTimer: ReturnType<typeof setTimeout> | null = null;
    // The handler reads the optional `tournamentId` only. `bout:advanced` names no tournament, so it passes.
    const onBracketsChanged = (payload?: Partial<SocketBracketsUpdated>) => {
      if (payload?.tournamentId && payload.tournamentId !== id) return;
      if (bracketTimer) clearTimeout(bracketTimer);
      bracketTimer = setTimeout(refreshBrackets, 300 + Math.random() * 1500);
    };
    // `as const`: `tsc` checks each name against `ServerToClientEvents` (the key of `SocketHandlers`).
    const bracketEvents = ['brackets:updated', 'bout:scored', 'bout:advanced', 'bout:created', 'bout:deleted', 'bout:updated', 'bout:status_changed', 'weighin:ended'] as const;
    const bracketHandlers: SocketHandlers = {};
    for (const event of bracketEvents) bracketHandlers[event] = onBracketsChanged;
    const unbindBrackets = bindSocket(socket, bracketHandlers);
    return () => {
      unbindData();
      unbindBrackets();
      if (regTimer) clearTimeout(regTimer);
      if (boutTimer) clearTimeout(boutTimer);
      if (bracketTimer) clearTimeout(bracketTimer);
    };
  }, [socket, id, reload, patchTournament, patchBouts, patchRegistrations, setWeighinProposal, refreshBrackets]);

  // Derived values
  // Scoped to THIS tournament, never the app-wide role (#406). The backend
  // keeps the two apart — `isTournamentManager` is the role guard,
  // `isUserTournamentTM` is the assignment — and the frontend used one name
  // for both. `pages/TournamentList.tsx` holds the app-wide one, and keeps
  // the name `isTournamentManager`, which is then honest.
  const managesThisTournament = tournament?.is_tournament_manager === true;
  const rings: Ring[] = tournament?.rings || [];
  const tournamentDays = (() => {
    if (!tournament?.date_start) return [];
    const days: string[] = [tournament.date_start.slice(0, 10)];
    if (tournament.date_end) {
      // enumerateDays works on local midnight. The old loop used
      // toISOString(), which converts to UTC and returns YESTERDAY after 21:00
      // at UTC+3 — so the day list could start on the wrong day during an
      // evening tournament. #322
      return enumerateDays(tournament.date_start, tournament.date_end);
    }
    return days;
  })();
  const isMultiDay = tournamentDays.length > 1;

  return {
    // No bare setter (#490): every write of the tournament goes through the
    // order guard above.
    tournament, patchTournament, refreshTournament,
    // No bare setter of the lists either (#664): a local write goes through
    // patchBouts() or patchRegistrations(), a fetch of the brackets through
    // loadBrackets(), and every one of them joins the ticket order above.
    bouts, patchBouts,
    registrations, patchRegistrations,
    referees,
    juryAssignments,
    managers, setManagers,
    brackets, loadBrackets, bracketsVersion,
    boutSubs, setBoutSub,
    weighins, setWeighins,
    weighinEndedDays, setWeighinEnded,
    weighinProposal, setWeighinProposal,
    loading, reload,
    loadError, retryLoad: loadInitial,
    // Derived
    managesThisTournament,
    rings,
    tournamentDays,
    isMultiDay,
  };
}
